import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageSquare, User, Bot, Clock } from "lucide-react";
import { format } from "date-fns";
import { useVoiceAgentMessages } from "@/hooks/useVoiceAgentAnalytics";

interface VoiceAgentSessionDetailsProps {
  sessionId: string | null;
  onClose: () => void;
}

const VoiceAgentSessionDetails = ({ sessionId, onClose }: VoiceAgentSessionDetailsProps) => {
  const { data: messages, isLoading } = useVoiceAgentMessages(sessionId);

  if (!sessionId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Session Details
          </CardTitle>
          <CardDescription>
            Select a session from the Sessions tab to view conversation details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[400px] flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No session selected</p>
              <p className="text-sm mt-1">Click "View" on a session to see the conversation</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Conversation Transcript
        </CardTitle>
        <CardDescription>
          Session ID: {sessionId.substring(0, 8)}...
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : messages && messages.length > 0 ? (
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${
                    message.role === "assistant" ? "justify-start" : "justify-end"
                  }`}
                >
                  {message.role === "assistant" && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-2 ${
                      message.role === "assistant"
                        ? "bg-muted text-foreground"
                        : "bg-primary text-primary-foreground"
                    }`}
                  >
                    <p className="text-sm">{message.content}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="h-3 w-3 opacity-60" />
                      <span className="text-xs opacity-60">
                        {format(new Date(message.timestamp), "HH:mm:ss")}
                      </span>
                      <Badge variant="outline" className="text-xs px-1 py-0">
                        {message.role}
                      </Badge>
                    </div>
                  </div>
                  {message.role === "user" && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <User className="h-4 w-4 text-primary-foreground" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="h-[400px] flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No messages in this session</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VoiceAgentSessionDetails;
